import React, { useState, useEffect } from 'react';
import { Menu, X, Globe, ChevronDown, LogIn, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../i18n/LanguageContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Navbar = ({ onOpenContact, onOpenLogin }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { t, currentLang, setLang, languages } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsOpen(false);
    }
  };

  const navLinks = [
    { label: t('nav.services'), id: 'services' },
    { label: t('nav.approach'), id: 'approach' },
    { label: t('nav.investors'), id: 'investors' },
    { label: t('nav.about'), id: 'about' },
  ];

  // Multi-language labels
  const getLoginLabel = () => {
    const labels = { en: 'Login', it: 'Accedi', 'es-es': 'Iniciar Sesión', 'es-latam': 'Iniciar Sesión' };
    return labels[currentLang] || labels.en;
  };
  const getStartedLabel = t('nav.requestCall') || 'Get Started';
  const loginLabel = getLoginLabel();

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ${
        scrolled 
          ? 'bg-white/95 backdrop-blur-sm shadow-md' 
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a 
            href="/" 
            data-testid="logo"
            className="flex items-center"
          >
            <img 
              src="/logo-hubner.png" 
              alt="HÜBNER Capital" 
              className="h-12 md:h-14 w-auto"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.id}
                data-testid={`nav-${link.id}`}
                onClick={() => scrollToSection(link.id)}
                className="font-body text-sm font-medium tracking-wide transition-colors text-foreground hover:text-gold"
              >
                {link.label}
              </button>
            ))}

            {/* Language Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  data-testid="language-switcher"
                  className="flex items-center gap-1 font-body text-sm font-medium transition-colors text-foreground hover:text-gold"
                >
                  <Globe className="h-4 w-4" />
                  <span>{languages.find(l => l.code === currentLang)?.flag}</span>
                  <ChevronDown className="h-3 w-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                align="end" 
                className="bg-white z-[150] max-h-[400px] overflow-y-auto"
                sideOffset={8}
              >
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    data-testid={`lang-${lang.code}`}
                    onClick={() => setLang(lang.code)}
                    className={`cursor-pointer ${currentLang === lang.code ? 'bg-muted' : ''}`}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Login Button */}
            <Button
              data-testid="nav-login"
              onClick={onOpenLogin}
              variant="ghost"
              className="font-body font-medium text-foreground hover:text-gold hover:bg-transparent"
            >
              <LogIn className="h-4 w-4 mr-2" />
              {loginLabel}
            </Button>

            {/* Get Started CTA */}
            <Button
              data-testid="nav-cta"
              onClick={onOpenContact}
              className="bg-gold text-navy hover:bg-gold/90 font-body font-medium rounded-md px-6"
            >
              {getStartedLabel}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 text-navy"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div 
            data-testid="mobile-menu"
            className="lg:hidden bg-white absolute top-20 left-0 right-0 shadow-lg"
          >
            <div className="px-6 py-4 space-y-4">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  data-testid={`mobile-nav-${link.id}`}
                  onClick={() => scrollToSection(link.id)}
                  className="block w-full text-left font-body text-base font-medium text-foreground hover:text-gold py-2"
                >
                  {link.label}
                </button>
              ))}
              
              {/* Mobile Language Switcher */}
              <div className="pt-4 border-t border-border">
                <p className="text-sm text-muted-foreground mb-2">
                  {currentLang?.startsWith('es') ? 'Idioma' : currentLang === 'it' ? 'Lingua' : 'Language'}
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {languages.slice(0, 6).map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => { setLang(lang.code); setIsOpen(false); }}
                      className={`text-xs py-2 px-3 rounded ${
                        currentLang === lang.code 
                          ? 'bg-navy text-white' 
                          : 'bg-muted text-foreground'
                      }`}
                    >
                      {lang.flag}
                    </button>
                  ))}
                </div>
              </div>

              {/* Mobile Login */}
              <Button
                data-testid="mobile-login"
                onClick={() => { onOpenLogin(); setIsOpen(false); }}
                variant="outline"
                className="w-full font-body font-medium rounded-md"
              >
                <LogIn className="h-4 w-4 mr-2" />
                {loginLabel}
              </Button>
              
              {/* Mobile CTA */}
              <Button
                data-testid="mobile-cta"
                onClick={() => { onOpenContact(); setIsOpen(false); }}
                className="w-full bg-gold text-navy hover:bg-gold/90 font-body font-medium rounded-md"
              >
                {getStartedLabel}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
